import { Component, type ErrorInfo, type ReactNode } from 'react'

import { Button } from './Button.js'

interface Props {
  children: ReactNode
}

interface State {
  failed: boolean
}

/**
 * The last line of defence.
 *
 * An eight-year-old should never see a stack trace, and a blank white screen is
 * worse — it reads as "I broke it". So a crash gets a friendly page and an
 * obvious way out. The detail still goes to the console for whoever debugs it.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled error in the app', error, info)
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children

    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-5 p-6 text-center">
        {/* Drawn rather than an emoji: a Raspberry Pi has no emoji font, so a
            teddy on the error screen is an empty rectangle on the television —
            and the error screen is exactly where nobody is in a position to
            work out what it was meant to be. */}
        <svg
          viewBox="0 0 24 24"
          className="text-amber size-(--text-hero)"
          fill="currentColor"
          aria-hidden="true"
        >
          <circle cx="6" cy="6.5" r="3.2" />
          <circle cx="18" cy="6.5" r="3.2" />
          <circle cx="12" cy="13" r="7.5" />
        </svg>
        <h1 className="text-(length:--text-title)">Oops — something went wobbly</h1>
        <p className="text-ink-muted">
          That wasn&rsquo;t your fault. Have another go, and it should be fine.
        </p>
        <Button onClick={() => window.location.assign('/')}>Back to the games</Button>
      </main>
    )
  }
}
